/**
 * Calcula el brillo promedio de un color (método alternativo más simple)
 * Retorna un valor entre 0 (negro) y 255 (blanco)
 *
 * @param hex - Color en formato hexadecimal (#RRGGBB)
 * @returns Brillo promedio (0-255)
 */
function getBrightness(hex: string): number {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Fórmula de brillo: (R*299 + G*587 + B*114) / 1000
  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * Determina si un color es oscuro o claro
 *
 * @param hex - Color en formato hexadecimal (#RRGGBB)
 * @returns true si el color es oscuro, false si es claro
 */
export function isDarkColor(hex: string): boolean {
  if (!hex) {
    // Si el color no es válido, asumir que es oscuro por defecto
    return true;
  }

  // Asegurar que el color tenga el formato correcto (#RRGGBB)
  let cleanHex = hex.trim();
  if (!cleanHex.startsWith('#')) {
    cleanHex = '#' + cleanHex;
  }

  // Validar formato hexadecimal
  if (!cleanHex.match(/^#[0-9A-Fa-f]{6}$/)) {
    return true; // Por defecto, asumir oscuro si no es válido
  }

  // Usar el método de brillo que es más directo y confiable
  // Si el brillo es menor a 128 (de 255), el color es oscuro
  // Colores claros como beige (#E4C99D ~224) y gris claro (#E0E0E0 ~224) tendrán brillo > 128
  const brightness = getBrightness(cleanHex);
  return brightness < 128;
}

/**
 * Obtiene el color de texto apropiado para un color de fondo dado
 *
 * @param backgroundColor - Color de fondo en formato hexadecimal (#RRGGBB)
 * @param darkTextColor - Color de texto para fondos claros (default: color semántico del proyecto)
 * @param lightTextColor - Color de texto para fondos oscuros (default: blanco)
 * @returns Color de texto apropiado
 */
export function getTextColorForBackground(
  backgroundColor: string | undefined,
  darkTextColor: string = '#000000', // Color oscuro fijo para fondos claros (negro)
  lightTextColor: string = '#ffffff', // Blanco para texto claro
): string {
  if (!backgroundColor) {
    return darkTextColor;
  }

  // Normalizar el color para asegurar formato correcto
  let normalizedColor = backgroundColor.trim();
  if (!normalizedColor.startsWith('#')) {
    normalizedColor = '#' + normalizedColor;
  }

  // Validar formato hexadecimal
  if (!normalizedColor.match(/^#[0-9A-Fa-f]{6}$/)) {
    return darkTextColor; // Si no es válido, usar color oscuro por defecto
  }

  // Calcular brillo y determinar si es oscuro o claro
  const brightness = getBrightness(normalizedColor);
  const isDark = brightness < 128;

  return isDark ? lightTextColor : darkTextColor;
}
